// Monetary Input Component
// Creates a formatted input field for monetary amounts with auto-select on focus

import { CURRENCY_OVERRIDES } from './utils.mjs';

/**
 * Returns the number of decimal places for a given ISO 4217 currency code.
 * Derived from Intl.NumberFormat — so JPY → 0, USD → 2, KWD → 3, etc.
 * @param {string} currency
 * @returns {number}
 */
export function getCurrencyFractionDigits(currency) {
    try {
        const uppercaseCurrency = currency.toUpperCase();
        if (CURRENCY_OVERRIDES && CURRENCY_OVERRIDES[uppercaseCurrency] !== undefined) {
            return CURRENCY_OVERRIDES[uppercaseCurrency].fractionSize;
        }
        return new Intl.NumberFormat('en-US', { style: 'currency', currency })
            .resolvedOptions()
            .maximumFractionDigits;
    } catch {
        return 2; // fallback for unknown/invalid currency codes
    }
}

/**
 * Format a numeric value as a monetary string with the correct decimal places.
 * @param {number} value
 * @param {number} fractionDigits - decimal places for this currency
 * @returns {string} e.g. "2,369.59" for USD, "2,370" for JPY
 */
function formatMonetaryValue(value, fractionDigits = 2) {
    if (!value || isNaN(value)) return '';
    return value.toLocaleString('en-US', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits
    });
}

/**
 * Create a monetary input element
 * @param {Object} options
 * @param {string} options.id
 * @param {string} options.name
 * @param {number} options.value - Initial numeric value
 * @param {string} options.placeholder
 * @param {string} options.currency - ISO 4217 currency code (default: 'USD')
 * @param {boolean} options.required
 * @param {string} options.className
 * @returns {HTMLInputElement}
 */
export function createMonetaryInput({
    id = '',
    name = '',
    value = 0,
    placeholder = '',
    currency = 'USD',
    required = false,
    className = ''
} = {}) {
    const fractionDigits = getCurrencyFractionDigits(currency);

    const input = document.createElement('input');
    input.type = 'text';
    input.inputMode = 'decimal';
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('autocorrect', 'off');
    input.setAttribute('spellcheck', 'false');
    input.setAttribute('data-form-type', 'other');
    if (id) input.id = id;
    if (name) input.name = name;
    if (className) input.className = className;
    if (required) input.required = true;
    input.placeholder = placeholder || (fractionDigits === 0 ? '0' : '0.' + '0'.repeat(fractionDigits));

    // Right-align text for monetary display
    input.style.textAlign = 'right';

    // Format the initial value
    if (value && value > 0) {
        input.value = formatMonetaryValue(value, fractionDigits);
    }

    // Select all text on focus
    input.addEventListener('focus', (e) => {
        e.target.select();
    });

    // Sanitize input as the user types
    input.addEventListener('input', (e) => {
        let val = e.target.value;

        if (fractionDigits === 0) {
            // Integer-only currencies (JPY, KRW, VND, etc.) — digits only
            val = val.replace(/[^\d]/g, '');
        } else {
            // Remove anything that isn't a digit or decimal point
            val = val.replace(/[^\d.]/g, '');

            // Ensure only one decimal point
            const parts = val.split('.');
            if (parts.length > 2) {
                val = parts[0] + '.' + parts.slice(1).join('');
            }

            // Enforce correct decimal places for this currency
            if (parts.length === 2 && parts[1].length > fractionDigits) {
                val = parts[0] + '.' + parts[1].substring(0, fractionDigits);
            }
        }

        e.target.value = val;
    });

    // Blur on Enter key
    input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
            e.target.blur();
        }
    });

    // Format on blur.
    // IMPORTANT: strip commas first — parseFloat("2,369.59") stops at the comma and returns 2.
    input.addEventListener('blur', (e) => {
        const numValue = parseFloat(e.target.value.replace(/,/g, '')) || 0;
        if (numValue > 0) {
            e.target.value = formatMonetaryValue(numValue, fractionDigits);
        } else {
            e.target.value = '';
        }
    });

    // Returns the numeric value of the current input (strips formatting commas)
    input.getRawValue = () => {
        return parseFloat(input.value.replace(/,/g, '')) || 0;
    };

    // Set value programmatically (formats it correctly)
    input.setMonetaryValue = (val) => {
        if (val && val > 0) {
            input.value = formatMonetaryValue(val, fractionDigits);
        } else {
            input.value = '';
        }
    };

    return input;
}

/**
 * Parse a formatted monetary string to a number (strips thousands commas)
 * @param {string} formattedValue
 * @returns {number}
 */
export function parseMonetaryValue(formattedValue) {
    if (!formattedValue) return 0;
    return parseFloat(formattedValue.replace(/,/g, '')) || 0;
}

/**
 * Splits a number into parts with precision adjustments.
 * Matches Flutter's SplitPalCurrencyUtils.splitNumber
 * @param {number} number - Total amount to split
 * @param {number} parts - Number of parts
 * @param {string} code - Currency code (e.g., 'USD')
 * @returns {number[]} Array of split amounts
 */
export function splitNumber(number, parts, code) {
    if (parts === 0) return [];

    // Use ISO 4217 decimal places for this currency (JPY=0, USD=2, KWD=3, …)
    const decimalDigits = getCurrencyFractionDigits(code);
    const factor = Math.pow(10, decimalDigits);

    // Step 1: Base part value
    const basePartValue = number / parts;

    // Step 2: Round base part value
    // (basePartValue * factor).round() / factor
    const roundedBasePartValue = Math.round(basePartValue * factor) / factor;

    // Create result array
    const result = new Array(parts).fill(roundedBasePartValue);

    // Step 3: Calculate sum
    // Use precise math for sum to match Flutter's behavior effectively
    let sumOfParts = result.reduce((a, b) => a + b, 0);
    // Fix JS floating point drift for the sum subtraction
    sumOfParts = parseFloat(sumOfParts.toFixed(decimalDigits));

    // Step 4: Adjustment
    let totalAdjustment = number - sumOfParts;
    // Fix floating point drift on adjustment
    totalAdjustment = parseFloat(totalAdjustment.toFixed(decimalDigits));

    // Step 5: Distribute adjustment
    const adjustmentCount = Math.round(Math.abs(totalAdjustment * factor));

    for (let i = 0; i < adjustmentCount; i++) {
        // Distribute 1 unit of precision
        const adj = totalAdjustment > 0 ? (1 / factor) : (-1 / factor);

        // Add and fix precision immediately
        let newVal = result[i] + adj;
        result[i] = parseFloat(newVal.toFixed(decimalDigits));
    }

    return result;
}

